import { AfterViewInit, Component, EventEmitter, Input, OnChanges, OnDestroy, Output, TemplateRef, ViewChild } from '@angular/core';
import { WhatsAppDeviceService, InstanceListItem } from '@shared/services/whatsapp-device.service';
import { WhatsAppQrConnectedPayload } from '../whatsapp-qr-modal/whatsapp-qr-modal.component';

export interface WhatsAppDevicePanelChange {
  instanceId: string | null;
  accountId: string | null;
}

@Component({
  selector: 'app-whatsapp-device-panel',
  templateUrl: './whatsapp-device-panel.component.html',
  styleUrl: './whatsapp-device-panel.component.scss',
})
export class WhatsAppDevicePanelComponent implements OnChanges, OnDestroy, AfterViewInit {
  @Input() instanceId: string | null = null;
  /** School edit: persist consent via API before QR. */
  @Input() schoolId: number | null = null;
  @Input() readOnly = false;

  @Output() deviceChange = new EventEmitter<WhatsAppDevicePanelChange>();
  @Output() chatvionBillingAcknowledged = new EventEmitter<void>();
  @Output() consentPersistError = new EventEmitter<string>();

  @ViewChild('qrModalHeader', { static: true }) qrModalHeader!: TemplateRef<unknown>;
  @ViewChild('qrModalBody', { static: true }) qrModalBody!: TemplateRef<unknown>;
  @ViewChild('ackModalHeader', { static: true }) ackModalHeader!: TemplateRef<unknown>;
  @ViewChild('ackModalBody', { static: true }) ackModalBody!: TemplateRef<unknown>;
  @ViewChild('ackModalFooter', { static: true }) ackModalFooter!: TemplateRef<unknown>;

  device: InstanceListItem | null = null;
  loading = false;
  loadError: string | null = null;
  showQrModal = false;
  showAckModal = false;
  qrModalAccountId: string | undefined;
  qrModalTemplate!: { header: TemplateRef<unknown>; body: TemplateRef<unknown> };
  ackModalTemplate!: { header: TemplateRef<unknown>; body: TemplateRef<unknown>; footer: TemplateRef<unknown> };
  qrModalSize = { width: '400px', height: '450px' };
  ackModalSize = { width: '520px', height: '340px' };
  removing = false;

  constructor(private deviceService: WhatsAppDeviceService) {}

  ngOnChanges(): void {
    if (this.instanceId) {
      this.resolveAndLoadDevice();
    } else {
      this.device = null;
      this.loadError = null;
    }
  }

  ngAfterViewInit(): void {
    this.qrModalTemplate = { header: this.qrModalHeader, body: this.qrModalBody };
    this.ackModalTemplate = {
      header: this.ackModalHeader,
      body: this.ackModalBody,
      footer: this.ackModalFooter,
    };
  }

  ngOnDestroy(): void {
    this.showQrModal = false;
  }

  closeQrModal(): void {
    this.showQrModal = false;
  }

  private resolveAndLoadDevice(): void {
    if (!this.instanceId) {
      this.device = null;
      return;
    }
    this.loading = true;
    this.loadError = null;
    this.deviceService.listInstances().subscribe({
      next: (data) => {
        const found = data.instances.find((i) => i.instanceId === this.instanceId);
        this.device = found ?? null;
        if (!found) {
          this.loadError = 'Device not found. It may have been removed or is from another tenant.';
          // Clear stale InstanceID in parent form so Save sends empty string → API sets IsActive=false, IsDeleted=true.
          if (!this.readOnly) {
            this.deviceChange.emit({ instanceId: null, accountId: null });
          }
        }
        this.loading = false;
      },
      error: (err) => {
        this.loading = false;
        this.loadError = err?.error?.message ?? err?.message ?? 'Failed to load devices';
        this.device = null;
      },
    });
  }

  onAddDevice(): void {
    this.showAckModal = true;
  }

  onAckCancel(): void {
    this.showAckModal = false;
  }

  onAckAgree(): void {
    this.showAckModal = false;
    this.qrModalAccountId = undefined;
    this.showQrModal = true;
  }

  onReconnect(): void {
    if (!this.device?.id) return;
    this.qrModalAccountId = this.device.id;
    this.showQrModal = true;
  }

  onQrConnected(payload: WhatsAppQrConnectedPayload): void {
    this.showQrModal = false;
    this.deviceChange.emit({
      instanceId: payload.instanceId || null,
      accountId: payload.accountId || null,
    });
    if (payload.instanceId) {
      this.instanceId = payload.instanceId;
      this.resolveAndLoadDevice();
    }
  }

  onQrClosed(): void {
    this.showQrModal = false;
  }

  onLogout(): void {
    if (!this.device?.id) return;
    this.loading = true;
    this.deviceService.logoutInstance(this.device.id).subscribe({
      next: () => {
        this.loading = false;
        this.resolveAndLoadDevice();
      },
      error: (err) => {
        this.loading = false;
        this.loadError = err?.error?.message ?? err?.message ?? 'Logout failed';
      },
    });
  }

  onRemove(): void {
    if (!this.device?.id || !confirm('Remove this WhatsApp device from the school? This cannot be undone.')) {
      return;
    }
    this.removing = true;
    this.deviceService.deleteInstance(this.device.id).subscribe({
      next: () => {
        this.removing = false;
        this.device = null;
        this.instanceId = null;
        this.deviceChange.emit({ instanceId: null, accountId: null });
      },
      error: (err) => {
        this.removing = false;
        this.loadError = err?.error?.message ?? err?.message ?? 'Remove failed';
      },
    });
  }

  get statusLabel(): string {
    if (!this.device) return '';
    const s = this.device.status;
    if (s === 'CONNECTED') return 'Connected';
    if (s === 'LOGGED_OUT' || s === 'DISCONNECTED' || s === 'NEEDS_QR') return 'Disconnected';
    return s;
  }

  get isConnected(): boolean {
    return this.device?.status === 'CONNECTED';
  }

  get showEmptyState(): boolean {
    return !this.device && !this.loading;
  }
}
